import React, { useState } from 'react';
import { ThemeSwitcher } from './ThemeSwitcher';
import { authClient } from '../lib/auth';

interface TopBarProps {
  onSearch: (query: string) => void;
  userEmail: string;
  onAdminClick?: () => void;
  onSettingsClick: () => void;
  gridLayout?: 'square' | 'justified';
  onGridLayoutChange?: (layout: 'square' | 'justified') => void;
}

export const TopBar: React.FC<TopBarProps> = ({
  onSearch,
  userEmail,
  onAdminClick,
  onSettingsClick,
  gridLayout = 'square',
  onGridLayoutChange,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const initial = (userEmail || '?')[0].toUpperCase();

  const { data: activeOrg } = authClient.useActiveOrganization();
  const { data: userOrgs } = authClient.useListOrganizations();

  const handleCopyTeamLink = () => {
    if (!activeOrg?.id) return;
    const url = `${window.location.origin}${window.location.pathname}?w=${activeOrg.id}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
    window.dispatchEvent(
      new CustomEvent('app:toast', {
        detail: {
          message: `Team link copied! Anyone opening this link will automatically switch to ${activeOrg.name} (${activeOrg.id}).`,
          type: 'success',
        },
      })
    );
  };

  return (
    <div
      className="app-topbar"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 1.5rem',
        height: '64px',
        background: 'var(--bg-primary)',
        borderBottom: '1px solid var(--border-color)',
        gap: '1rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: 'var(--accent-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontFamily: 'Outfit',
          }}
        >
          F
        </div>
        <h1
          style={{
            fontSize: '1.25rem',
            margin: 0,
            color: 'var(--text-primary)',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontWeight: 600,
          }}
        >
          FileSharez
        </h1>

        {/* Active Workspace Badge & 1-Click Copy Team Link */}
        {activeOrg && (
          <div
            className="desktop-only"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              marginLeft: '0.75rem',
              borderLeft: '1px solid var(--border-color)',
              paddingLeft: '0.75rem',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)',
                borderRadius: '20px',
                padding: '0.2rem 0.6rem',
                fontSize: '0.8rem',
              }}
            >
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{activeOrg.name}</span>
              <span
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  color: 'var(--text-secondary)',
                  background: 'rgba(124, 58, 237, 0.08)',
                  padding: '0.1rem 0.35rem',
                  borderRadius: '4px',
                  userSelect: 'all',
                  cursor: 'pointer',
                }}
                title="Unique Workspace ID (double-click to select all)"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(activeOrg.id);
                  setCopiedLink(true);
                  setTimeout(() => setCopiedLink(false), 2000);
                }}
              >
                {activeOrg.id}
              </span>
            </div>

            <button
              className="btn btn-ghost"
              onClick={handleCopyTeamLink}
              title="Copy unique workspace link (?w=org...)"
              style={{
                fontSize: '0.75rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                color: copiedLink ? '#22c55e' : 'var(--text-secondary)',
              }}
            >
              <span>{copiedLink ? '✓' : '🔗'}</span>
              <span>{copiedLink ? 'Copied' : 'Copy Team Link'}</span>
            </button>
          </div>
        )}
      </div>

      <div style={{ flex: 1, maxWidth: '600px', margin: '0 1rem' }} className="desktop-only">
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search your photos"
            onChange={(e) => onSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '0.6rem 1rem 0.6rem 2.5rem',
              borderRadius: '999px',
              border: 'none',
              background: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              outline: 'none',
              fontSize: '0.9rem',
            }}
          />
          <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }}>
            🔍
          </span>
          <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', opacity: 0.5, cursor: 'pointer' }}>
            ⚙️
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        {onGridLayoutChange && (
          <div style={{ display: 'flex', background: 'var(--bg-tertiary)', borderRadius: '8px', padding: '2px' }}>
            <button
              className="btn btn-ghost"
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '6px',
                background: gridLayout === 'square' ? 'var(--bg-primary)' : 'transparent',
                boxShadow: gridLayout === 'square' ? 'var(--shadow-sm)' : 'none',
                color: gridLayout === 'square' ? 'var(--text-primary)' : 'var(--text-secondary)',
              }}
              onClick={() => onGridLayoutChange('square')}
              title="Square Grid"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
            </button>
            <button
              className="btn btn-ghost"
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '6px',
                background: gridLayout === 'justified' ? 'var(--bg-primary)' : 'transparent',
                boxShadow: gridLayout === 'justified' ? 'var(--shadow-sm)' : 'none',
                color: gridLayout === 'justified' ? 'var(--text-primary)' : 'var(--text-secondary)',
              }}
              onClick={() => onGridLayoutChange('justified')}
              title="Justified Grid"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="6"></rect>
                <rect x="3" y="15" width="8" height="6"></rect>
                <rect x="15" y="15" width="6" height="6"></rect>
              </svg>
            </button>
          </div>
        )}
        <ThemeSwitcher />
        <button className="btn btn-primary" style={{ padding: '0.5rem 1rem', borderRadius: '999px' }}>
          <span style={{ marginRight: '0.5rem' }}>☁️</span> Upload
        </button>
        {onAdminClick && (
          <button className="btn btn-ghost desktop-only" onClick={onAdminClick}>
            Administration
          </button>
        )}
        <div style={{ position: 'relative' }}>
          <div
            onClick={() => setShowDropdown(!showDropdown)}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'var(--accent-primary)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            {initial}
          </div>
          {showDropdown && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '0.5rem',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                boxShadow: 'var(--shadow-md)',
                width: '230px',
                zIndex: 100,
                padding: '0.5rem 0',
              }}
            >
              <div
                style={{
                  padding: '0.5rem 1rem',
                  borderBottom: '1px solid var(--border-color)',
                  marginBottom: '0.25rem',
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {userEmail}
              </div>

              {/* Multi-workspace Switcher List in Dropdown */}
              {userOrgs && userOrgs.length > 1 && (
                <div style={{ borderBottom: '1px solid var(--border-color)', padding: '0.4rem 0' }}>
                  <div
                    style={{
                      padding: '0.2rem 1rem',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: 'var(--text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Switch Workspace
                  </div>
                  {userOrgs.map((org) => {
                    const isSelected = org.id === activeOrg?.id;
                    return (
                      <div
                        key={org.id}
                        onClick={async () => {
                          setShowDropdown(false);
                          if (!isSelected) {
                            await authClient.organization.setActive({ organizationId: org.id });
                            window.location.reload();
                          }
                        }}
                        style={{
                          padding: '0.45rem 1rem',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: isSelected ? 'rgba(124, 58, 237, 0.08)' : 'transparent',
                          color: isSelected ? 'var(--accent-primary)' : 'var(--text-primary)',
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {org.name}
                        </span>
                        {isSelected && <span style={{ fontSize: '0.8rem' }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              )}

              <div
                onClick={() => {
                  setShowDropdown(false);
                  onSettingsClick();
                }}
                style={{ padding: '0.5rem 1rem', cursor: 'pointer', transition: 'background 0.2s', fontSize: '0.9rem' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                Settings
              </div>
              <div
                onClick={async () => {
                  setShowDropdown(false);
                  await authClient.signOut();
                  window.location.reload();
                }}
                style={{ padding: '0.5rem 1rem', cursor: 'pointer', transition: 'background 0.2s', fontSize: '0.9rem', color: '#ef4444' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                Sign Out
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
